import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {DataGrid, GridColDef, GridRowSelectionModel} from '@mui/x-data-grid';
import {formatDate} from 'src/utils/common';
import message from 'src/components/Message';
import Spin from 'src/components/Spin';
import {AccessGroup, OrgUser} from 'src/apis/autoGenerate/types';
import {
  accessGroupServiceDetail,
  accessGroupServiceFindOrgUsers,
  accessGroupServiceAddOrgUsers,
  accessGroupServiceDeleteOrgUsers,
} from 'src/apis/autoGenerate/services';
import AddOrgUsersToAccessGroupModal from 'src/components/Modals/AddOrgUsersToAccessGroup';

export default function ManageAccessGroupOrgUsers() {
  const navigate = useNavigate();
  const {accessGroupId} = useParams<{accessGroupId: string}>();
  const [loading, setLoading] = useState(false);
  const [targetGroup, setTargetGroup] = useState<AccessGroup>();
  const [groupOrgUsers, setGroupOrgUsers] = useState<OrgUser[]>([]);
  const [tenantListLoading, setTenantListLoading] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState<GridRowSelectionModel>([]);
  const [addOrgUserModalOpen, setAddOrgUserModalOpen] = useState(false);

  useEffect(() => {
    if (accessGroupId) {
      getAccessGroupDetail();
      getGroupOrgUsers();
    }
  }, [accessGroupId]);

  const getAccessGroupDetail = async () => {
    if (!accessGroupId) return;
    try {
      const res = await accessGroupServiceDetail(accessGroupId);
      // The API directly returns the AccessGroup object, not wrapped in data.accessGroup
      setTargetGroup(res.data || res);
    } catch (error) {
      message.error('Failed to load access group details');
      navigate('/devices/accessGroup');
    }
  };

  const getGroupOrgUsers = async (name?: string) => {
    if (!accessGroupId) return;
    setTenantListLoading(true);
    try {
      const res = await accessGroupServiceFindOrgUsers(accessGroupId, name);
      const orgUsers = res.data?.users || [];
      setGroupOrgUsers(orgUsers);
    } catch (error) {
      message.error('Failed to load org users');
    } finally {
      setTenantListLoading(false);
    }
  };

  const handleAddOrgUsers = async (orgUserIds: string[]) => {
    if (!accessGroupId) return;
    setLoading(true);
    try {
      await accessGroupServiceAddOrgUsers({
        accessGroupId,
        userIds: orgUserIds,
      });
      message.success('Org Users added successfully');
      setAddOrgUserModalOpen(false);
      getGroupOrgUsers();
    } catch (error: any) {
      message.error(error.message || 'Failed to add org users');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveOrgUsers = async () => {
    if (!accessGroupId || selectedRowIds.length === 0) return;
    setLoading(true);
    try {
      await accessGroupServiceDeleteOrgUsers({
        accessGroupId,
        userIds: selectedRowIds as string[],
      });
      message.success('Org Users removed successfully');
      setSelectedRowIds([]);
      getGroupOrgUsers();
    } catch (error: any) {
      message.error(error.message || 'Failed to remove org users');
    } finally {
      setLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'firstName',
      headerName: 'Name',
      width: 220,
      renderCell: val => {
        const orgUser = val.row;
        return (
          <Stack>
            <Typography variant="body2" sx={{fontSize: 14, fontWeight: 500}}>
              {`${orgUser.firstName || ''} ${orgUser.lastName || ''}`.trim() || orgUser.name || ''}
            </Typography>
            <Typography variant="body2" sx={{color: theme => theme.palette.text.secondary}}>
              {orgUser.email || ''}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'roles',
      headerName: 'Role',
      width: 200,
      renderCell: val => {
        const orgUser = val.row;
        if (orgUser.roles && orgUser.roles.length > 0) {
          return (
            <Stack spacing={0.5}>
              {orgUser.roles.map((role: any, index: number) => (
                <Box key={role.id || index}>{role.name || ''}</Box>
              ))}
            </Stack>
          );
        }
        return <Box sx={{color: theme => theme.palette.text.secondary}}>No Role</Box>;
      },
    },
    {
      field: 'createdAt',
      headerName: 'Create Date',
      width: 150,
      renderCell: val => {
        return <Box>{formatDate(val.value)}</Box>;
      },
    },
  ];

  if (!targetGroup) {
    return <Spin spinning={true} />;
  }

  return (
    <Box>
      <Spin spinning={loading} />
      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
        <Stack sx={{display: 'flex', alignItems: 'center'}} spacing={2} direction="row">
          <IconButton
            onClick={() => {
              navigate(-1);
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{fontWeight: 500}}>
            Manage Org Users - {targetGroup.name}
          </Typography>
        </Stack>
        <Box>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setAddOrgUserModalOpen(true)}
            sx={{mr: 2}}
          >
            Add Org Users
          </Button>
          <Button
            variant="outlined"
            startIcon={<DeleteIcon />}
            disabled={groupOrgUsers.length < 1 || selectedRowIds.length < 1}
            sx={{
              borderRadius: 16,
              height: 40,
              color: '#324054',
              borderColor: '#324054',
              '&:hover': {color: '#0f1317', borderColor: '#0f1317'},
            }}
            onClick={handleRemoveOrgUsers}
          >
            Remove
          </Button>
        </Box>
      </Box>

      <Box>
        <DataGrid
          loading={tenantListLoading}
          disableColumnFilter
          disableColumnMenu
          disableColumnSelector
          rows={groupOrgUsers}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: {page: 0, pageSize: 10},
            },
          }}
          onRowSelectionModelChange={res => {
            setSelectedRowIds(res);
          }}
          slots={{
            noRowsOverlay: () => (
              <Box textAlign="center" my={2}>
                No Org Users
              </Box>
            ),
          }}
          pageSizeOptions={[5, 10]}
          checkboxSelection
          autoHeight
        />
      </Box>

      {addOrgUserModalOpen && (
        <AddOrgUsersToAccessGroupModal
          open={addOrgUserModalOpen}
          targetGroup={targetGroup}
          existingOrgUsers={groupOrgUsers}
          handleCancel={() => setAddOrgUserModalOpen(false)}
          handleConfirm={handleAddOrgUsers}
        />
      )}
    </Box>
  );
}
